import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useMemo } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import countryList from 'react-select-country-list';
import 'flag-icons/css/flag-icons.min.css';

export const Hero = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [emailError, setEmailError] = useState("");
  const [nameError, setNameError] = useState("");
  const [whatsappError, setWhatsappError] = useState("");
  const [countryCode, setCountryCode] = useState("BR");
  const { toast } = useToast();

  const countries = useMemo(() => {
    return countryList().getData().map(country => ({
      code: country.value,
      name: country.label,
      // Mapeamento de alguns códigos de telefone mais comuns
      phone: {
        'BR': '55',
        'PT': '351',
        'US': '1',
        'ES': '34',
        'DE': '49',
        // ... outros países
      }[country.value] || ''
    }));
  }, []);

  const validateEmail = (email: string) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!re.test(email)) {
      setEmailError("Por favor, insira um email válido");
    } else {
      setEmailError("");
    }
  };

  const validateName = (name: string) => {
    if (name.length < 3) {
      setNameError("Nome deve ter pelo menos 3 caracteres");
    } else {
      setNameError("");
    }
  };

  const validateWhatsapp = (whatsapp: string) => {
    const re = /^[0-9]{8,11}$/;
    if (!re.test(whatsapp)) {
      setWhatsappError("Digite apenas números");
    } else {
      setWhatsappError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedCountry = countries.find(c => c.code === countryCode);
    const phoneCode = selectedCountry?.phone || '55'; // fallback para Brasil
    
    try {
      const { error } = await supabase
        .from('leads')
        .insert([{
          full_name: fullName,
          email,
          whatsapp: `${phoneCode}${whatsapp}` // usando o código do telefone
        }]);

      if (error) throw error;
      
      toast({
        title: "Sucesso!",
        description: "Seus dados foram registrados com sucesso.",
      });

      setFullName("");
      setEmail("");
      setWhatsapp("");
    } catch (error) {
      console.error('Erro:', error);
      
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível salvar seus dados. Tente novamente.",
      });
    }
  };

  return (
    <div className="flex-1 relative bg-cover bg-center" style={{
      backgroundImage: 'url("/lovable-uploads/f09fcd74-7f8a-4f6b-81ef-4801635b5a55.png")'
    }}>
      <div className="absolute inset-0 bg-black/50" />
      
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 [text-shadow:_2px_2px_0_rgb(14_165_233),_4px_4px_0_rgb(15_23_42)] font-serif">
                Automatize
                <br />
                seu Futuro
              </h1>
              <p className="text-xl text-white/90 mb-8">
                Revolucione a maneira como você e o seu negócio<br />
                se relacionam com clientes, parceiros e o resto do mundo<br />
                usando soluções de Automação alimentadas por IA.
                <br /><br />
                O Futuro é agora!
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-md p-8 rounded-lg border border-white/20">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-white mb-2">Junte-se a Nós!</h2>
                  <p className="text-white/80">Receba novidades e atualizações</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Input
                      type="text"
                      placeholder="Nome completo"
                      value={fullName}
                      onChange={(e) => {
                        setFullName(e.target.value);
                        validateName(e.target.value);
                      }}
                      className={`bg-white/10 border-white/20 text-white placeholder:text-white/50 ${
                        nameError ? "border-red-500" : ""
                      }`}
                      required
                    />
                    {nameError && (
                      <p className="text-red-500 text-sm mt-1">{nameError}</p>
                    )}
                  </div>
                  <div>
                    <Input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        validateEmail(e.target.value);
                      }}
                      className={`bg-white/10 border-white/20 text-white placeholder:text-white/50 ${
                        emailError ? "border-red-500" : ""
                      }`}
                      required
                    />
                    {emailError && (
                      <p className="text-red-500 text-sm mt-1">{emailError}</p>
                    )}
                  </div>
                  <div>
                    <div className="flex gap-2">
                      <Select defaultValue="BR" onValueChange={setCountryCode}>
                        <SelectTrigger className="w-[180px] bg-white/10 border-white/20 text-white">
                          <SelectValue>
                            <span className={`fi fi-${countryCode.toLowerCase()} mr-2`} />
                            {countries.find(c => c.code === countryCode)?.name}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map(country => (
                            <SelectItem key={country.code} value={country.code}>
                              <span className={`fi fi-${country.code.toLowerCase()} mr-2`} />
                              {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="tel"
                        placeholder="WhatsApp"
                        value={whatsapp}
                        onChange={(e) => {
                          setWhatsapp(e.target.value);
                          validateWhatsapp(e.target.value);
                        }}
                        className={`flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50 ${
                          whatsappError ? "border-red-500" : ""
                        }`}
                        required
                      />
                    </div>
                    {whatsappError && (
                      <p className="text-red-500 text-sm mt-1">{whatsappError}</p>
                    )}
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    variant="secondary"
                    disabled={!!nameError || !!emailError || !!whatsappError}
                  >
                    Fique por dentro!
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};